import { Text, RadioCards, Flex, IconButton } from "@radix-ui/themes";
import { SkuSection } from "@/shared/api";
import PolygonCrop from "./polygon-crop";
import { CameraSkuSectionProjection } from "../lib/types";
import { InfoItem } from "@/components/info-item";
import { twMerge } from "tailwind-merge";
import { getImageSrc } from "@/shared/api/helpers";
import { Cross1Icon } from "@radix-ui/react-icons";

type SkuSidebarProps = {
  selectedPolygon: SkuSection | null;
  selectedSkuSectionId: number | null;
  onProjectionClick?: (cameraId: string) => void;
  projections?: CameraSkuSectionProjection | null;
  selectedCamera?: string | null;
  onClose?: () => void;
};

export function SkuSidebar({
  selectedPolygon,
  selectedSkuSectionId,
  onProjectionClick,
  projections,
  selectedCamera,
  onClose,
}: SkuSidebarProps) {
  if (!selectedPolygon) {
    return (
      <div className="p-2">
        {selectedSkuSectionId && (
          <>
            <Text size="1" className="text-tuna" color="red">
              SKU Section <Text weight="bold">{selectedSkuSectionId}</Text> not
              found in current camera !
            </Text>
            <br />
          </>
        )}
        <Text size="1" className="text-tuna">
          Please select any SKU section
        </Text>
      </div>
    );
  }

  return (
    <>
      <Flex justify="between" className="p-2">
        <Text size="2" weight="bold" color="gray" highContrast>
          Camera SKU Section Info
        </Text>
        {onClose && (
          <IconButton
            variant="ghost"
            color="gray"
            className="p-2"
            onClick={onClose}
          >
            <Cross1Icon />
          </IconButton>
        )}
      </Flex>
      <div className="p-2">
        <div className="flex flex-column items-center pb-2">
          <div className="min-w-20 min-h-20 object-contain">
            <img
              className="w-20 h-20 object-contain"
              src={selectedPolygon.variant.image}
            />
          </div>
          <Text
            size="1"
            weight="medium"
            className="ml-2"
            color="gray"
            highContrast
          >
            {selectedPolygon.variant?.name}
          </Text>
        </div>
        <div>
          <InfoItem label="Barcode" value={selectedPolygon.variant?.barcode} />
          <InfoItem label="SKU ID" value={selectedPolygon.variant?.id} />
          <InfoItem label="Camera SKU Section ID" value={selectedPolygon?.id} />
          <InfoItem
            label="SKU Section ID"
            value={selectedPolygon?.sku_section_id}
          />
        </div>
      </div>
      {projections?.length ? (
        <div className="px-2 py-1">
          <Text size="1" weight="bold" color="gray" highContrast>
            Projections
          </Text>

          <RadioCards.Root
            value={selectedCamera || ""}
            onValueChange={(newValue) => {
              const projection = projections.find(
                ({ cameraId }) => cameraId === newValue
              );

              if (projection) onProjectionClick?.(projection.cameraId);
            }}
            disabled
            columns={{ initial: "2" }}
            gap="1"
          >
            {projections.map((projection) => {
              const imageUrl = getImageSrc(
                projection.skuSection.original_image
              );
              const points = projection.skuSection.polygon_points;
              const originalWidth = projection.skuSection.width;
              const originalHeight = projection.skuSection.height;
              const isSelected = projection.cameraId === selectedCamera;

              return (
                <div className="relative" key={projection.skuSection.id}>
                  <RadioCards.Item
                    key={projection.skuSection.id}
                    value={projection.cameraId}
                    className={twMerge(
                      "basis-1/2 block m-0 p-0 text-left w-full h-full !rounded-md before:!rounded-md after:!rounded-md",
                      isSelected ? "" : ""
                    )}
                  >
                    <div className="aspect-square p-1">
                      {!projection.skuSection.original_image ? (
                        <div className="w-full h-full bg-gray-200 flex justify-center items-center rounded-md">
                          <Text size="1" color="gray">
                            Camera image doesn't exist
                          </Text>
                        </div>
                      ) : (
                        <PolygonCrop
                          imageUrl={imageUrl}
                          polygonPoints={points}
                          originalWidth={originalWidth}
                          originalHeight={originalHeight}
                        />
                      )}
                    </div>

                    <Flex px="1" pb="1" gap="1">
                      <Text size="1" as="span" color="gray">
                        Camera:
                      </Text>
                      <Text size="1" as="span" color="gray" highContrast>
                        {projection.cameraId}
                      </Text>
                    </Flex>
                  </RadioCards.Item>
                </div>
              );
            })}
          </RadioCards.Root>
        </div>
      ) : null}
    </>
  );
}
